import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Bell, Check, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const AlertsBell = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ alerts: [], unread: 0 });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts");
      setData(data);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const markRead = async () => {
    try {
      await api.post("/alerts/read");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="relative">
      <button
        data-testid="alerts-bell"
        onClick={() => setOpen(!open)}
        title="Alerts"
        className="relative h-9 w-9 rounded-full border border-zinc-700 bg-zinc-900 flex items-center justify-center text-zinc-200 hover:border-emerald-600 hover:text-emerald-300 transition-colors duration-200"
      >
        <Bell className="h-4 w-4" />
        {data.unread > 0 && (
          <span
            data-testid="alerts-unread-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
          >
            {data.unread}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <>
            <button
              type="button"
              aria-label="Close alerts"
              data-testid="alerts-backdrop"
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[90] bg-black/70 sm:bg-transparent"
            />
            <div
              data-testid="alerts-panel"
              className="fixed inset-y-0 right-0 z-[95] w-[88vw] max-w-[360px] overflow-y-auto border-l border-zinc-800 bg-[#0B0C0D] shadow-2xl sm:inset-y-auto sm:top-16 sm:right-6 sm:w-[380px] sm:max-h-[60vh] sm:rounded-sm sm:border"
            >
          <div className="sticky top-0 flex items-center gap-2 px-3 py-2.5 border-b border-zinc-800 bg-[#0B0C0D]">
            <span className="text-xs uppercase tracking-[0.12em] font-semibold text-zinc-200">
              Alerts
            </span>
            <div className="flex-1" />
            <button
              data-testid="alerts-mark-read"
              onClick={markRead}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] font-semibold text-zinc-300 hover:text-emerald-300 transition-colors duration-200"
            >
              <Check className="h-3 w-3" /> Mark read
            </button>
            <button
              data-testid="alerts-close"
              onClick={() => setOpen(false)}
              className="ml-2 text-zinc-400 hover:text-zinc-100 transition-colors duration-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {data.alerts.length === 0 && (
            <p className="px-3 py-8 text-center text-xs text-zinc-400" data-testid="alerts-empty">
              No alerts yet.
            </p>
          )}
          {data.alerts.map((a) => (
            <div
              key={a.alert_id}
              data-testid={`alert-${a.alert_id}`}
              className={`px-3 py-2.5 border-b border-zinc-900 ${
                a.read ? "opacity-60" : "bg-amber-500/[0.06]"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.1em] font-bold text-amber-300">
                  {a.type}
                </span>
                {a.symbol && (
                  <span className="font-mono text-[11px] text-zinc-300">{a.symbol}</span>
                )}
                <div className="flex-1" />
                <span className="font-mono text-[10px] text-zinc-500">{a.created_at_ist}</span>
              </div>
              <p className="mt-1 text-xs text-zinc-200 leading-snug">{a.message}</p>
            </div>
          ))}
            </div>
          </>,
          document.body
        )}
    </div>
  );
};
